import { useHotkeys } from "react-hotkeys-hook";
import { useAnnotationsPanel } from "~/components/build/Show/lib/useAnnotationsPanel";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";

/**
 * Closes the docked annotations panel when Escape is pressed, mirroring the
 * job drawer's own Escape-to-close behaviour.
 *
 * The job drawer takes priority: while it's open its own Escape handler closes
 * it first, so this only closes the panel once the drawer is gone. A single
 * Escape therefore closes whichever of the two is "on top" (drawer first), and
 * a second Escape closes the panel.
 *
 * Enabled only when the panel can actually render and is open
 * (`BuildPageDockedAnnotations` + open + no drawer). `react-hotkeys-hook` fires
 * on buttons by default — important because the toggle button keeps focus after
 * the click that opened the panel — while still skipping text-entry fields
 * (`input`/`textarea`/`select`) so it doesn't hijack Escape mid-typing.
 *
 * The `defaultPrevented` check skips the handler once another layer has already
 * consumed the Escape. Radix dismissable layers (e.g. the annotations filter
 * dropdown) listen in the capture phase and `preventDefault()` when they close,
 * so an Escape that dismisses an open dropdown won't also close the whole panel.
 * It lives inside the callback (not the `ignoreEventWhen` option) because
 * `react-hotkeys-hook` deep-compares the options object to decide when to
 * re-subscribe — an inline option function would change identity every render
 * and re-subscribe in a loop, whereas the callback is held in a ref.
 */
export function useCloseAnnotationsPanelOnEscape() {
  const { isOpen, setOpen } = useAnnotationsPanel();
  const { sid, jid, open } = useQueryParams();

  const drawerOpen = Boolean(sid || jid) && open !== "false";

  useHotkeys(
    "escape",
    (event) => {
      if (event.defaultPrevented) {
        return;
      }
      setOpen(false);
    },
    { enabled: Features.BuildPageDockedAnnotations && isOpen && !drawerOpen },
  );
}
